import { useCallback } from 'react';
import {
  useStartEmailContent,
  useDeleteEmailContent,
  type EmailDocumentType,
  type EmailContentResponse,
} from 'src/meeting-note/hooks/useGenerateEmailContent';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type EmailDocumentContext = { clientId?: string; familyId?: string };

export const useEmailDocument = () => {
  const { startEmailContent, isGenerating } = useStartEmailContent();
  const { deleteEmailContent, isDeleting } = useDeleteEmailContent();

  const handleEmailClick = useCallback(
    async (
      documentId: string,
      documentType: EmailDocumentType,
      context: EmailDocumentContext,
    ): Promise<EmailContentResponse | null> => {
      try {
        const response = await startEmailContent({ ...context, documentId, documentType });
        if (!response?.id) throw new Error('Failed to generate email content');

        return response;
      } catch (e) {
        notifyAndLogError(
          'Failed to generate email',
          'An error occurred while generating email content. Please try again later.',
          e,
          { ...context, documentId, documentType },
        );
        return null;
      }
    },
    [startEmailContent],
  );

  const handleDeleteEmail = useCallback(
    async (emailContentId: string) => {
      try {
        await deleteEmailContent(emailContentId);
      } catch {
        // Best-effort cleanup
      }
    },
    [deleteEmailContent],
  );

  return {
    handleEmailClick,
    handleDeleteEmail,
    isGenerating,
    isDeleting,
  };
};
